import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  type Classroom,
  type ClassroomApprovers,
  type ClassroomDraft,
  type ClassroomEdits,
} from '#/state/classrooms/types'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {classroomProcedure, classroomQuery} from './client'
import {type ClassroomApiError} from './errors'
import {classroomKeyPrefixes, classroomKeys, invalidateClassroom} from './keys'
import {
  type ClassroomViewer,
  type ClassroomViewJson,
  toApproversJson,
  toClassroom,
  toViewer,
} from './map'

const NS = 'app.taderok.classroom'
const MAX_LIST_PAGES = 10

export type ClassroomRoom = {classroom: Classroom; viewer: ClassroomViewer}

export function toRoom(view: ClassroomViewJson): ClassroomRoom {
  return {classroom: toClassroom(view), viewer: toViewer(view)}
}

/** Puts a server view into the room cache and returns the mapped classroom. */
export function cacheClassroomView(
  queryClient: QueryClient,
  view: ClassroomViewJson,
): Classroom {
  const room = toRoom(view)
  queryClient.setQueryData(classroomKeys.room(view.id), room)
  queryClient.setQueryData(
    classroomKeys.byRoute(view.owner.handle, view.handle),
    room,
  )
  return room.classroom
}

export function useClassroomQuery(id: string | undefined) {
  const agent = useAgent()
  return useQuery({
    queryKey: classroomKeys.room(id ?? ''),
    queryFn: async () => {
      const res = await classroomQuery<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.getClassroom`,
        {id},
      )
      return toRoom(res.classroom)
    },
    staleTime: STALE.SECONDS.THIRTY,
    enabled: !!id,
  })
}

export function useClassroomByRouteQuery({
  owner,
  handle,
  code,
}: {
  owner: string
  handle: string
  code?: string
}) {
  const agent = useAgent()
  return useQuery({
    queryKey: classroomKeys.byRoute(owner, handle),
    queryFn: async () => {
      const res = await classroomQuery<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.getClassroom`,
        {owner, handle, code},
      )
      return toRoom(res.classroom)
    },
    staleTime: STALE.SECONDS.THIRTY,
    enabled: !!owner && !!handle,
  })
}

export function useClassroomsForDidQuery(did: string | undefined) {
  const agent = useAgent()
  return useQuery({
    queryKey: classroomKeys.forDid(did ?? '', 'both'),
    queryFn: async () => {
      const list = async (relation: 'owned' | 'member') => {
        const all: Classroom[] = []
        let cursor: string | undefined
        for (let page = 0; page < MAX_LIST_PAGES; page++) {
          const res = await classroomQuery<{
            classrooms: ClassroomViewJson[]
            cursor?: string
          }>(agent, `${NS}.listClassrooms`, {
            actor: did,
            relation,
            limit: 100,
            cursor,
          })
          all.push(...res.classrooms.map(toClassroom))
          cursor = res.cursor
          if (!cursor) break
        }
        return all
      }
      const [owned, member] = await Promise.all([list('owned'), list('member')])
      return {owned, member}
    },
    staleTime: STALE.SECONDS.THIRTY,
    enabled: !!did,
  })
}

// The cover is not sent: it is a blob upload, which this layer does not do.
function fieldsOf(
  input: Omit<ClassroomDraft, 'owner' | 'cover'> | ClassroomEdits,
) {
  const {cover: _cover, ...rest} = input as ClassroomEdits
  return rest
}

export function useCreateClassroomMutation() {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<Classroom, ClassroomApiError, ClassroomDraft>({
    mutationFn: async draft => {
      const {owner: _owner, ...rest} = draft
      const res = await classroomProcedure<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.createClassroom`,
        fieldsOf(rest),
      )
      return cacheClassroomView(queryClient, res.classroom)
    },
    onSuccess: created => invalidateClassroom(queryClient, created.id),
  })
}

export function useUpdateClassroomMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<Classroom, ClassroomApiError, ClassroomEdits>({
    mutationFn: async edits => {
      const res = await classroomProcedure<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.updateClassroom`,
        {id, ...fieldsOf(edits)},
      )
      return cacheClassroomView(queryClient, res.classroom)
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useUpdateClassroomSettingsMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<
    Classroom,
    ClassroomApiError,
    {
      studentApprovers?: ClassroomApprovers
      parentApprovers?: ClassroomApprovers
      parentsCanJoin?: boolean
      autoAcceptWithCode?: boolean
    }
  >({
    mutationFn: async ({studentApprovers, parentApprovers, ...rest}) => {
      const res = await classroomProcedure<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.updateSettings`,
        {
          id,
          ...rest,
          ...(studentApprovers && {
            studentApprovers: toApproversJson(studentApprovers),
          }),
          ...(parentApprovers && {
            parentApprovers: toApproversJson(parentApprovers),
          }),
        },
      )
      return cacheClassroomView(queryClient, res.classroom)
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useSetModeratorMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, {did: string; value: boolean}>({
    mutationFn: async ({did, value}) => {
      await classroomProcedure(agent, `${NS}.setModerator`, {id, did, value})
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useRegenerateCodeMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<Classroom, ClassroomApiError, void>({
    mutationFn: async () => {
      const res = await classroomProcedure<{classroom: ClassroomViewJson}>(
        agent,
        `${NS}.regenerateCode`,
        {id},
      )
      return cacheClassroomView(queryClient, res.classroom)
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useDeleteClassroomMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, void>({
    mutationFn: async () => {
      await classroomProcedure(agent, `${NS}.deleteClassroom`, {id})
    },
    onSuccess: () => {
      // A refetch of a deleted room would only fail with NotFound
      queryClient.removeQueries({queryKey: classroomKeyPrefixes.room(id)})
      queryClient.removeQueries({queryKey: classroomKeyPrefixes.byRoute()})
      queryClient.removeQueries({queryKey: classroomKeyPrefixes.members(id)})
      queryClient.removeQueries({queryKey: classroomKeyPrefixes.content(id)})
      return invalidateClassroom(queryClient, id)
    },
  })
}
